import {hasValidImageSrc} from '../leap/utils/utils'

/**
 * Width requested for the poster and the size the MP4 rendition is encoded at, used when no
 * CMS poster is given so the placeholder image roughly matches the video it precedes.
 */
export const BRIGHTCOVE_MP4_WIDTH = 1280

/** CDN the signed MP4 is served from - preconnecting to it shaves the TLS handshake off an eager video's start. */
export const BRIGHTCOVE_CDN_PRECONNECT_HOST =
    'https://fastly-signed-us-east-1-prod.brightcovecdn.com'

const getBrightcoveBaseUrl = (accountId, videoId) =>
    `/bv/${accountId}/${encodeURIComponent(videoId)}`

/**
 * Builds the URL of the faststart MP4 rendition of a Brightcove video, served by our own server.
 *
 * @param {string} accountId - Brightcove account id.
 * @param {string} videoId - Brightcove video id or `ref:` reference id.
 * @returns {string} Relative URL.
 * @example
 * getBrightcoveMp4Url('1234', 'ref:hero') // '/bv/1234/ref%3Ahero.mp4'
 */
export const getBrightcoveMp4Url = (accountId, videoId) =>
    `${getBrightcoveBaseUrl(accountId, videoId)}.mp4`

/**
 * Builds the URL of a Brightcove video poster resized to a given width.
 *
 * @param {string} accountId - Brightcove account id.
 * @param {string} videoId - Brightcove video id or `ref:` reference id.
 * @param {number} [width] - Requested poster width, defaults to the MP4 rendition width.
 * @returns {string} Relative URL.
 * @example
 * getBrightcovePosterUrl('1234', 'ref:hero') // '/bv/1234/ref%3Ahero/poster.jpg?w=1280'
 */
export const getBrightcovePosterUrl = (accountId, videoId, width = BRIGHTCOVE_MP4_WIDTH) =>
    `${getBrightcoveBaseUrl(accountId, videoId)}/poster.jpg?w=${width}`

/**
 * Builds the DirectVideo props that play a Brightcove video from our own server.
 * @param {string} accountId - Brightcove account id
 * @param {string} brightcoveId - Brightcove video id or `ref:` reference id
 * @param {Object} [poster] - CMS poster image object; defaults to our poster endpoint
 * @param {string} [title] - Video title, used as the alt text of our poster
 * @returns {Object} The `sources`, `resolveSharedLibrary`, `poster` and `preconnect` props, or
 * empty `sources` without an account or video id, so DirectVideo renders nothing.
 */
export const getBrightcoveVideoProps = (accountId, brightcoveId, poster, title) => {
    if (!accountId || !brightcoveId) return {sources: []}
    return {
        // root-relative, identical between SSR and hydration
        sources: [{src: getBrightcoveMp4Url(accountId, brightcoveId), type: 'video/mp4'}],
        resolveSharedLibrary: false,
        poster: hasValidImageSrc(poster)
            ? poster
            : {src: getBrightcovePosterUrl(accountId, brightcoveId), alt: title ?? ''},
        preconnect: BRIGHTCOVE_CDN_PRECONNECT_HOST
    }
}
